import {AppError} from '../util/errors'

/**
 * `xr.js` is loaded async and sets `window.XR8` only after it has awaited the
 * preloaded chunks (SLAM is 5.5 MB, so this is genuinely slow on mobile data).
 * It then dispatches `xrloaded`.
 */
const READY_EVENT = 'xrloaded'

const DEFAULT_TIMEOUT_MS = 20_000

/**
 * Runtime proof that the hand-written declarations in `types/xr8.d.ts` match
 * the engine build we actually loaded.
 *
 * Without this, a version mismatch shows up as `undefined is not a function`
 * somewhere deep in a frame callback. With it, we get one clear error naming
 * the missing member at startup. This single check is what makes the rest of
 * the codebase safe to write against the ambient types.
 */
const assertXr8Shape: (value: unknown) => asserts value is Xr8Global = (value) => {
  const missing: string[] = []
  const xr8 = value as Record<string, unknown> | null

  const requireFn = (path: string, fn: unknown): void => {
    if (typeof fn !== 'function') missing.push(path)
  }
  const namespace = (key: string): Record<string, unknown> | undefined => {
    const ns = xr8?.[key]
    if (typeof ns !== 'object' || ns === null) {
      missing.push(key)
      return undefined
    }
    return ns as Record<string, unknown>
  }

  if (typeof xr8 !== 'object' || xr8 === null) {
    throw new AppError('ENGINE_SHAPE_MISMATCH', 'window.XR8 is not an object')
  }

  requireFn('addCameraPipelineModules', xr8['addCameraPipelineModules'])
  requireFn('run', xr8['run'])
  requireFn('pause', xr8['pause'])
  requireFn('resume', xr8['resume'])
  requireFn('isPaused', xr8['isPaused'])

  requireFn('GlTextureRenderer.pipelineModule', namespace('GlTextureRenderer')?.['pipelineModule'])

  const threejs = namespace('Threejs')
  requireFn('Threejs.pipelineModule', threejs?.['pipelineModule'])
  requireFn('Threejs.xrScene', threejs?.['xrScene'])

  const controller = namespace('XrController')
  requireFn('XrController.pipelineModule', controller?.['pipelineModule'])
  requireFn('XrController.recenter', controller?.['recenter'])
  requireFn('XrController.hitTest', controller?.['hitTest'])
  requireFn('XrController.updateCameraProjectionMatrix', controller?.['updateCameraProjectionMatrix'])

  requireFn('XrConfig.device', namespace('XrConfig')?.['device'])

  if (missing.length > 0) {
    throw new AppError('ENGINE_SHAPE_MISMATCH', `missing XR8 members: ${missing.join(', ')}`)
  }
}

/**
 * Resolves once the engine is loaded and its shape has been verified.
 *
 * Rejects on timeout rather than spinning forever - a blocked CDN, an in-app
 * browser that silently refuses the script, or a bad deploy all present as
 * "nothing ever happens", which is the worst possible failure mode on a
 * campaign landing page.
 */
export const whenXr8Ready = (
  options: {readonly timeoutMs?: number} = {}
): Promise<Xr8Global> => {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS

  return new Promise<Xr8Global>((resolve, reject) => {
    let settled = false

    const finish = (): void => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      window.removeEventListener(READY_EVENT, onLoaded)
      try {
        assertXr8Shape(window.XR8)
        resolve(window.XR8)
      } catch (error) {
        reject(error instanceof Error ? error : new AppError('ENGINE_SHAPE_MISMATCH'))
      }
    }

    const onLoaded = (): void => finish()

    const timer = window.setTimeout(() => {
      if (settled) return
      settled = true
      window.removeEventListener(READY_EVENT, onLoaded)
      reject(new AppError('ENGINE_LOAD_FAILED', `no ${READY_EVENT} within ${timeoutMs}ms`))
    }, timeoutMs)

    // The script is async, so it may already have finished before we get here.
    if (window.XR8) finish()
    else window.addEventListener(READY_EVENT, onLoaded, {once: true})
  })
}
